import { addDays, addMonths, type PlanOccurrence } from "@klantpunt/shared";

/**
 * Tijdschaal van de MJOP-planning (docs/ASSETS-MJOP.md, "Planning"): jaren over de horizon, of
 * ingezoomd op kwartalen, maanden of weken met een venster dat je verschuift.
 */
export const ZOOMS = ["year", "quarter", "month", "week"] as const;
export type Zoom = (typeof ZOOMS)[number];
export const ZOOM_LABELS: Record<Zoom, string> = { year: "Jaar", quarter: "Kwartaal", month: "Maand", week: "Week" };

export const MONTHS = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
export const fmtDay = (iso: string | null) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m! - 1]} ${y}`;
};

export interface Period {
  /** Begin (inclusief) en einde (exclusief), JJJJ-MM-DD. */
  from: string;
  to: string;
  year: number;
  label: string;
  sub: string;
  /** Vandaag valt in deze periode. */
  now: boolean;
  /** Eerste periode van een nieuw jaar (scheidingslijn). */
  yearStart: boolean;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Begin van de periode waarin `iso` valt; weken beginnen op maandag. */
export function startOf(zoom: Zoom, iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  if (zoom === "quarter") return `${y}-${pad(Math.floor((m - 1) / 3) * 3 + 1)}-01`;
  if (zoom === "month") return `${y}-${pad(m)}-01`;
  if (zoom === "week") {
    const wd = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
    return addDays(iso.slice(0, 10), -wd);
  }
  return `${y}-01-01`;
}

/** ISO-weeknummer (week 1 bevat de eerste donderdag van het jaar). */
export function isoWeek(iso: string): number {
  const t = new Date(`${iso}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7) + 3);
  const jan4 = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((t.getTime() - jan4.getTime()) / 864e5 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
}

/** Aantal kolommen per zoom: 12 kwartalen (3 jaar), 12 maanden, 13 weken. */
const WINDOW: Record<Exclude<Zoom, "year">, number> = { quarter: 12, month: 12, week: 13 };

export function periods(zoom: Zoom, anchor: string, fromYear: number, toYear: number, today: string): Period[] {
  const raw: Omit<Period, "now" | "yearStart">[] = [];
  if (zoom === "year") {
    for (let y = fromYear; y <= toYear; y++)
      raw.push({ from: `${y}-01-01`, to: `${y + 1}-01-01`, year: y, label: String(y), sub: "" });
  } else {
    let f = startOf(zoom, anchor);
    for (let i = 0; i < WINDOW[zoom]; i++) {
      const to = zoom === "week" ? addDays(f, 7) : addMonths(f, zoom === "quarter" ? 3 : 1);
      const [y, m, d] = f.split("-").map(Number) as [number, number, number];
      raw.push({
        from: f,
        to,
        year: y,
        label:
          zoom === "quarter"
            ? `K${Math.floor((m - 1) / 3) + 1}`
            : zoom === "month"
              ? MONTHS[m - 1]!
              : `wk ${isoWeek(f)}`,
        sub: zoom === "week" ? `${d} ${MONTHS[m - 1]}` : String(y),
      });
      f = to;
    }
  }
  return raw.map((p, i) => ({
    ...p,
    now: p.from <= today && today < p.to,
    yearStart: zoom !== "year" && i > 0 && raw[i - 1]!.year !== p.year,
  }));
}

/** Venster een stap verschuiven: kwartalen per jaar, maanden per 3, weken per 4. */
export function shift(zoom: Zoom, anchor: string, dir: 1 | -1): string {
  const f = startOf(zoom, anchor);
  if (zoom === "week") return addDays(f, 28 * dir);
  return addMonths(f, (zoom === "quarter" ? 12 : 3) * dir);
}

/** "okt 2026 – sep 2027", "9 nov 2026 – 7 feb 2027" of "2026–2035". */
export function rangeLabel(zoom: Zoom, cols: Period[]): string {
  const f = cols[0];
  const l = cols[cols.length - 1];
  if (!f || !l) return "";
  if (zoom === "year") return `${f.year}–${l.year}`;
  if (zoom === "week") return `${fmtDay(f.from)} – ${fmtDay(addDays(l.to, -1))}`;
  return `${f.label} ${f.sub} – ${l.label} ${l.sub}`;
}

/** Uitvoermomenten met datum binnen een periode. Posten (zonder datum) tellen alleen per jaar. */
export const datedIn = (occ: readonly PlanOccurrence[], p: Period) =>
  occ.filter((o) => o.date && !o.skipped && o.date >= p.from && o.date < p.to);

/** Bedrag in een kolom: per jaar alles (behalve overgeslagen), ingezoomd alleen met datum. */
export function amountIn(occ: readonly PlanOccurrence[], p: Period, zoom: Zoom): number {
  if (zoom === "year") return occ.filter((o) => o.year === p.year && !o.skipped).reduce((s, o) => s + o.amountCents, 0);
  return datedIn(occ, p).reduce((s, o) => s + o.amountCents, 0);
}
